/**
 * TypeScript interfaces for the LEADER Cloud consistent API response format
 * Based on the CONSISTENT_WRAPPER pattern documented in consistent_api_responses.md
 */

// Core API Response Interface
export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  pagination: PaginationInfo | null;
  message: string;
  timestamp: string;
  error?: ErrorInfo;
}

// Pagination Information
export interface PaginationInfo {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

// Error Information
export interface ErrorInfo {
  code: string;
  details: { [key: string]: any };
}

// Common Error Codes
export enum ErrorCodes {
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  DATABASE_ERROR = 'DATABASE_ERROR'
}

// Custom Error Class
export class LeaderCloudError extends Error {
  constructor(
    message: string,
    public errorInfo?: ErrorInfo,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'LeaderCloudError';
  }
}

// API Response Utility Types
export type ApiSuccessResponse<T> = ApiResponse<T> & { success: true; data: T };
export type ApiErrorResponse = ApiResponse<null> & { success: false; error: ErrorInfo };

// Domain-specific types (extend as needed)
export interface User {
  user_id: string;
  username: string;
  email?: string;
  name?: string;
  role: string;
  access_tags?: { [key: string]: any };
  created_at?: string;
  updated_at?: string;
}

export interface Source {
  source_id: string;
  name: string;
  description?: string;
  supplier_id?: string;
  platform_id?: string;
  sensor_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Supplier {
  supplier_id: string;
  name: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Channel {
  channel_id: string;
  name: string;
  description?: string;
  source_id?: string;
  pipeline_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ResourceAccess {
  granted_at: string;
  granted_by: string;
  access_type: 'READ' | 'WRITE';
  resource_id: string;
  resource_type: string;
  user_id: string;
}

export interface Record {
  record_id: string;
  leader_id?: string;
  source_id?: string;
  channel_id?: string;
  timestamp: string;
  data: any;
  metadata?: { [key: string]: any };
}

// Query/Filter types
export interface RecordsQueryFilters {
  leader_id?: string;
  source_id?: string;
  channel_id?: string;
  start_time?: string;
  end_time?: string;
  limit?: number;
  page?: number;
  // Support nested source filters
  'source.source_id'?: string;
  'source.name'?: string;
  'source.supplier_id'?: string;
  'source.platform_id'?: string;
  'source.sensor_id'?: string;
}

export interface QueryResult<T> {
  records: T[];
  count: number;
  limit_applied?: number;
  query_method?: string;
  pagination?: PaginationInfo;
}

// Records API response structure (actual API format)
export interface RecordsApiResponse<T> extends ApiResponse<T[]> {
  count?: number;
  limit_applied?: number;
  query_method?: string;
  sort_order?: string;
}
